import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useGlobalConfig } from "context/GlobalContext";
import { BglPrimarySmallButton, BglSecondarySmallButton } from "components/button/BglButtons";
import { StringRegistryApis } from "../api/stringRegistryAPI";
import { StringTemplate } from "../types/stringRegistryTypes";

interface Props {
  open: boolean;
  onClose: () => void;
  template: StringTemplate;
  onCloned: () => void;
}

export const StringCloneTemplateDialog = ({ open, onClose, template, onCloned }: Props) => {
  const { rc } = useGlobalConfig();
  const [name, setName] = useState(`${template.name} (Copy)`);
  const [description, setDescription] = useState(template.description || "");
  const [isSaving, setIsSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Seed the fields from the template being cloned every time the dialog opens
  // — the useState initializers only run on first mount, so this must not rely
  // on the caller unmounting the dialog between uses.
  useEffect(() => {
    if (!open) return;
    setName(`${template.name} (Copy)`);
    setDescription(template.description || "");
    setErr(null);
  }, [open, template.documentId, template.name, template.description]);

  const handleClose = () => {
    setErr(null);
    onClose();
  };

  const handleClone = async () => {
    if (!name.trim()) {
      setErr("Template name is required");
      return;
    }
    setIsSaving(true);
    setErr(null);
    try {
      const response = await StringRegistryApis.cloneTemplate(rc, template.documentId, name.trim(), description.trim() || undefined);
      if (response.error) {
        setErr(response.error.message || "Failed to clone");
      } else {
        onCloned();
        handleClose();
      }
    } catch (e: any) {
      setErr(e.message || "Clone failed");
    } finally {
      setIsSaving(false);
    }
  };

  const valueCount = template.valueCount ?? template.items?.length ?? 0;
  // Server writes one row per entry; a big template takes a while and the
  // request must not be abandoned midway, leaving a half-populated clone
  const isSlowClone = valueCount > 200;

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : handleClose}
      disableEscapeKeyDown={isSaving}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ContentCopyIcon color="primary" />
        Clone string template
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a copy of &quot;{template.name}&quot; including string entries and locale overrides.
        </Typography>
        {err && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
            {err}
          </Alert>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField label="New template name" fullWidth required value={name} onChange={(e) => setName(e.target.value)} />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Box>

        {isSlowClone && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Copying {valueCount} string entries can take a few minutes. Keep this dialog
            open until it finishes — closing it early leaves a partly-filled template.
          </Alert>
        )}

        {isSaving && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              Copying {valueCount} string entr{valueCount === 1 ? "y" : "ies"}…
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <BglSecondarySmallButton onClick={handleClose} disabled={isSaving}>
          Cancel
        </BglSecondarySmallButton>
        <BglPrimarySmallButton onClick={handleClone} disabled={isSaving || !name.trim()}>
          {isSaving ? "Cloning…" : "Clone"}
        </BglPrimarySmallButton>
      </DialogActions>
    </Dialog>
  );
};
